import { useMutation } from "@apollo/client";
import { Draggable } from "@hello-pangea/dnd";
import { CheckIcon } from "@heroicons/react/solid";
import { withErrorBoundary } from "@sentry/react";
import { uniqueId } from "lodash";
import uniqBy from "lodash/uniqBy";
import moment from "moment";
import { KeyboardEvent, useCallback, useRef, useState } from "react";
import TextareaAutosize from "react-textarea-autosize";
import {
  MeetingViewMeetingNodeNewPageFragmentFragment,
  NotesRequirement,
  TopicNodeNewPageFragmentFragment,
} from "types/graphql-schema";

import TopicAssignee from "@apps/topic-assignee/topic-assignee";
import TopicDropdownMenu from "@apps/topic-dropdown-menu/topic-dropdown-menu";
import updateTopicMutation from "@apps/topic-sidebar/graphql/update-topic-title-description-mutation";
import { currentUserVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize } from "@components/button/button-constants";
import Error from "@components/error/error";
import Tooltip from "@components/tooltip/tooltip";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { isEmptyValue } from "@components/wysiwyg/helpers";
import { delay, tempCacheIdPrefix, topicState } from "@helpers/constants";
import { classNames } from "@helpers/css";
import {
  isEnterEvent,
  parseStringToJSON,
  removeLineBreaks,
  showConfetti,
} from "@helpers/helpers";

import createOrUpdateTopicMutation from "../graphql/create-or-update-topic-mutation";
import TopicNodeFragment from "../graphql/topic-node-fragment";
import { meetingPaddingClassName } from "../helpers";
import DiscussionNotes from "./discussion-notes";
import IndividualNotesContainer from "./individual-notes-container";

// Preserves in-progress edit when a temp topic remounts with a real ID.
// Cleared synchronously during render so it can't leak to other topics.
let pendingNewTopicEdit: { title: string } | null = null;

const TopicContent = ({
  snapshot,
  provided,
  topic,
  meeting,
  opensInSidebar,
  isInExtension,
  isInPresentation,
  currentPageMeeting = null,
  index,
  isDiscussed = false,
  onToggleDiscussed,
  autoFocus = false,
}: {
  topic: TopicNodeNewPageFragmentFragment;
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  index?: number;
  opensInSidebar?: boolean;
  isInExtension?: boolean;
  isInPresentation?: boolean;
  currentPageMeeting?: MeetingViewMeetingNodeNewPageFragmentFragment | null;
  isDiscussed?: boolean;
  onToggleDiscussed?: () => void;
  autoFocus?: boolean;
  snapshot?: any;
  provided?: any;
}) => {
  const currentUser = currentUserVar();
  const isSavingRef = useRef(false);
  const isTemporaryTopic = String(topic.id).includes(tempCacheIdPrefix);
  const canEditTitle = topic.canUpdate.permission && !topic.isMandatory;

  // Consume pending edit synchronously during render, then capture in a ref.
  // useRef retains the value from the first invocation even if React strict mode
  // double-invokes the component body, so the value survives correctly.
  const restoredEdit =
    pendingNewTopicEdit && topic.title === "New Topic"
      ? pendingNewTopicEdit.title
      : null;
  if (restoredEdit !== null) {
    pendingNewTopicEdit = null;
  }
  const restoredEditRef = useRef(restoredEdit);

  const [hasSharedNotesContent, setHasSharedNotesContent] = useState(
    () => !isEmptyValue(parseStringToJSON(topic.discussionNotes)),
  );

  const [isEditing, setIsEditing] = useState(
    () =>
      restoredEditRef.current !== null ||
      (isTemporaryTopic && topic.title === "New Topic" && canEditTitle) ||
      (autoFocus && topic.title === "New Topic" && canEditTitle),
  );
  const [editTitle, setEditTitle] = useState(
    () => restoredEditRef.current ?? topic.title,
  );
  const isPlaceholderTitle = editTitle === "New Topic";

  // Save edit state during render (not in a cleanup effect) so it's available
  // immediately when the real topic renders. React's render phase for the new
  // component runs before the old component's cleanup effects, so a cleanup-based
  // save would be too late.
  if (isTemporaryTopic) {
    if (isEditing) {
      pendingNewTopicEdit = { title: editTitle };
    } else {
      pendingNewTopicEdit = null;
    }
  }

  const [createTopic] = useMutation(createOrUpdateTopicMutation);

  const editInputRef = useCallback((node: HTMLTextAreaElement | null) => {
    if (!node) return;
    node.focus();
    // Check the actual DOM value rather than derived state to avoid
    // re-selecting text if the ref callback re-runs during a re-render.
    if (node.value === "New Topic") {
      node.setSelectionRange(0, 0);
    } else {
      const len = node.value.length;
      node.setSelectionRange(len, len);
    }
  }, []);

  const [saveTopic] = useMutation(updateTopicMutation, {
    update: (cache, { data }) => {
      const topicId = cache.identify(topic);
      if (topicId) {
        cache.modify({
          id: topicId,
          fields: {
            title() {
              return data.createOrUpdateTopic.topic.title;
            },
          },
        });
      }
    },
  });

  const handleSave = useCallback(() => {
    if (isSavingRef.current) return Promise.resolve();
    isSavingRef.current = true;
    const trimmed = editTitle.trim();
    let savePromise = Promise.resolve();
    if (trimmed && trimmed !== topic.title) {
      savePromise = saveTopic({
        variables: { topicId: topic.id, title: trimmed },
        optimisticResponse: {
          createOrUpdateTopic: {
            topic: { ...topic, title: trimmed },
            __typename: "CreateOrUpdateTopicMutation",
          },
        },
      }).then(() => {});
    } else {
      setEditTitle(topic.title);
    }
    setIsEditing(false);
    requestAnimationFrame(() => {
      isSavingRef.current = false;
    });
    return savePromise;
  }, [editTitle, topic, saveTopic]);

  const handleClickTitle = useCallback(() => {
    if (!canEditTitle || isEditing) return;
    // Check if there's already an input with user text (e.g. from a pending edit restore)
    const existingInput = document.querySelector(
      `#meeting-${meeting.id}-topic-${topic.id} [aria-label="Edit topic title"]`,
    ) as HTMLInputElement | null;
    if (existingInput?.value && existingInput.value !== "New Topic") return;
    setEditTitle(topic.title);
    setIsEditing(true);
  }, [canEditTitle, isEditing, topic.title, meeting.id, topic.id]);

  const handleCreateTopicBelow = useCallback(() => {
    const tempId = uniqueId(`${tempCacheIdPrefix}-topic`);
    const isFormalOneonone = !!meeting.meetingGroup?.isFormalOneonone;
    const topicData = {
      id: tempId,
      title: "New Topic",
      description: "",
      includesIndividualNotes: isFormalOneonone,
      includesIndividualNotesForCurrentUser: isFormalOneonone,
      includesSharedNotes: !isFormalOneonone,
      discussionNotes: null,
      isMandatory: false,
      canUpdate: { __typename: "PermissionNode", permission: true },
      canDelete: { __typename: "PermissionNode", permission: true },
      created: moment().format(),
      state: topicState.open,
      eventChannelName: `topic-${tempId}`,
      creator: currentUser,
      assignee: currentUser,
      previousTopic: null,
      linkedTemplateTopic: null,
      notesRequirement: null,
      comments: { totalCount: 0, __typename: "CommentNodeConnection" },
      individualNotes: {
        edges: [],
        __typename: "IndividualNoteNodeConnection",
      },
      copiedFrom: null,
      __typename: "TopicNode",
    };
    createTopic({
      variables: {
        meetingId: meeting.id,
        meetingGroupId: meeting.meetingGroup?.id,
        title: "New Topic",
        afterTopicId: topic.id,
        includesIndividualNotes: isFormalOneonone,
        includesSharedNotes: !isFormalOneonone,
      },
      optimisticResponse: {
        createOrUpdateTopic: {
          topic: topicData,
          __typename: "CreateOrUpdateTopicMutation",
        },
      },
      onCompleted: ({ createOrUpdateTopic }) => {
        setTimeout(() => {
          const container = document.querySelector(
            `#meeting-${meeting.id}-topic-${createOrUpdateTopic.topic.id}`,
          );
          if (!container) return;
          // If user is already editing with custom text, don't override
          const editInput = container.querySelector(
            '[aria-label="Edit topic title"]',
          ) as HTMLInputElement | null;
          if (editInput && editInput.value && editInput.value !== "New Topic") {
            return;
          }
          const titleEl = container.querySelector(
            '[aria-label="Topic title"]',
          ) as HTMLElement | null;
          titleEl?.click();
        }, delay.waitTillFocus);
      },
      update(cache, response) {
        const newTopic = response.data.createOrUpdateTopic.topic;
        const createdTopicCacheNode = cache.writeFragment({
          data: newTopic,
          fragment: TopicNodeFragment,
          fragmentName: "TopicNodeNewPageFragment",
          variables: { meetingGroupId: meeting.meetingGroup?.id },
        });
        const meetingCacheId = cache.identify(meeting);
        const afterTopicRef = cache.identify({
          __typename: "TopicNode",
          id: topic.id,
        });
        cache.modify({
          id: meetingCacheId,
          fields: {
            topics(cachedTopics) {
              const mergedEdges = cachedTopics.edges.reduce(
                (memo: any[], cachedTopic: any) => {
                  if (cachedTopic.node.__ref === afterTopicRef) {
                    return [
                      ...memo,
                      cachedTopic,
                      {
                        node: createdTopicCacheNode,
                        __typename: "TopicNodeEdge",
                      },
                    ];
                  }
                  return [...memo, cachedTopic];
                },
                [],
              );
              return { edges: uniqBy(mergedEdges, ({ node }) => node.__ref) };
            },
          },
        });
      },
    });
  }, [createTopic, meeting, topic.id, currentUser]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (isEnterEvent(e) && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSave().then(() => handleCreateTopicBelow());
    } else if (isEnterEvent(e)) {
      e.preventDefault();
      handleSave().then(() => {
        const container = document.querySelector(
          `#meeting-${meeting.id}-topic-${topic.id}`,
        );
        if (!container) return;

        if (topic.includesSharedNotes) {
          const sharedEditor = (
            container.querySelector(
              ".js-topic-discussion-notes-input",
            ) as Element & { editor?: { isEmpty: boolean; chain: () => any } }
          )?.editor;
          if (sharedEditor?.isEmpty) {
            sharedEditor.chain().focus("start").run();
            return;
          }
        }

        if (
          !topic.includesSharedNotes &&
          (topic.includesIndividualNotes ||
            topic.includesIndividualNotesForCurrentUser)
        ) {
          const individualEditor = (
            container.querySelector(
              '[aria-label="Meeting topic individual notes"] .js-topic-discussion-notes-input',
            ) as Element & { editor?: { isEmpty: boolean; chain: () => any } }
          )?.editor;
          if (individualEditor?.isEmpty) {
            individualEditor.chain().focus("start").run();
            return;
          }
        }
      });
    } else if (e.key === "Escape") {
      setEditTitle(topic.title);
      setIsEditing(false);
    }
  };

  let notesHelperText = "";
  if (topic.notesRequirement === NotesRequirement.OneParticipant) {
    notesHelperText = "At least one participant must add notes.";
  } else if (topic.notesRequirement === NotesRequirement.AllParticipants) {
    notesHelperText = "All participants must add notes.";
  }

  const isInFullScreen = index === undefined;
  return (
    <li
      key={topic.id}
      id={`meeting-${meeting.id}-topic-${topic.id}`}
      className={classNames(
        "group/topic bg-white",
        "-mx-6",
        topic.isMandatory && "relative js-mandatory-topic",
        snapshot?.isDragging && "bg-purple-50/90 shadow-xl rounded-xl",
      )}
      {...(provided?.draggableProps || {})}
      ref={provided?.innerRef}
      aria-label={`Topic ${topic.title}`}
      data-testid={`container-topic-${topic.id}`}
    >
      <div
        className={classNames(
          "flex w-full relative group",
          meetingPaddingClassName,
        )}
      >
        <div className="flex-1 flex gap-1">
          {!isInFullScreen && (
            <span
              className={classNames(
                "cursor-grab text-gray-400 absolute py-0.5 px-0 left-[2px] top-1 bg-gray-100 hover:bg-gray-200 rounded hidden sm:group-hover:block",
                snapshot?.isDragging && "block",
                isInExtension && "hidden",
              )}
              {...(provided?.dragHandleProps || {})}
              aria-label="Topic drag handle"
            >
              <TopicflowIcon type="drag" className="h-4 w-4 " />
            </span>
          )}

          {/* TEXT */}
          <div className="flex-1">
            <TextareaAutosize
              ref={isEditing ? editInputRef : undefined}
              minRows={1}
              readOnly={!isEditing}
              tabIndex={isEditing ? 0 : -1}
              className={classNames(
                "w-full border-none rounded m-0 pr-6 text-base font-medium leading-6 focus:outline-none focus:ring-0 resize-none bg-transparent",
                isInPresentation && "text-xl",
                !isEditing && canEditTitle && "cursor-text",
                isPlaceholderTitle && "text-gray-300",
              )}
              value={isEditing ? removeLineBreaks(editTitle) : topic.title}
              onChange={(e) => {
                if (isPlaceholderTitle) {
                  // Clear "New Topic" on first keystroke and keep only the new input
                  const newVal = e.target.value.replace("New Topic", "");
                  setEditTitle(newVal);
                } else {
                  setEditTitle(e.target.value);
                }
              }}
              onKeyDown={isEditing ? handleKeyDown : undefined}
              onBlur={isEditing ? handleSave : undefined}
              onClick={!isEditing ? handleClickTitle : undefined}
              aria-label={isEditing ? "Edit topic title" : "Topic title"}
            />
          </div>

          {!isInExtension && topic.isMandatory && (
            <div>
              <Tooltip
                text={`The creator of this template has marked the topic as mandatory and it cannot be edited. ${notesHelperText}`}
              >
                <span className="text-xs text-amber-700 bg-amber-100 px-1 py-px rounded-md ml-1">
                  Mandatory
                </span>
              </Tooltip>
            </div>
          )}
        </div>

        {/* STATE & ACTIONS */}
        {/* wrapping div is necessary to keep icon align to top */}
        {!isInExtension && (
          <div className="flex items-start gap-1">
            <div className="invisible group-hover/topic:visible">
              <TopicAssignee
                topic={topic}
                size={ButtonSize.medium}
                assignableUsers={meeting.participants?.edges.map(
                  (edge) => edge?.node?.user,
                )}
              />
            </div>
            <div className="invisible group-hover/topic:visible">
              <TopicDropdownMenu
                meetingGroup={meeting.meetingGroup}
                topic={topic}
                meeting={meeting}
                portal={opensInSidebar}
                currentPageMeeting={currentPageMeeting}
                isLastTopic={meeting.topics?.edges.length === 1}
                size={ButtonSize.medium}
              />
            </div>
            <Button
              size={ButtonSize.medium}
              onClick={() => {
                if (!isDiscussed) {
                  showConfetti({
                    confettiNumber: 40,
                    confettiRadius: 4,
                    confettiColors: [
                      "#10b981",
                      "#34d399",
                      "#6ee7b7",
                      "#a7f3d0",
                    ],
                  });
                }
                onToggleDiscussed?.();
              }}
              className={classNames(
                isDiscussed &&
                  "border-transparent bg-emerald-50 text-emerald-600 hover:bg-emerald-100",
                !isDiscussed && "invisible group-hover/topic:visible",
              )}
            >
              <CheckIcon className="h-4 w-4 shrink-0" />
              Discussed
            </Button>
          </div>
        )}
      </div>

      <div className="relative">
        {topic.includesSharedNotes && (
          <DiscussionNotes
            topic={topic}
            meeting={meeting}
            className={isInPresentation ? "text-lg" : ""}
            onContentChange={setHasSharedNotesContent}
            hasContent={hasSharedNotesContent}
            onModEnter={handleCreateTopicBelow}
          />
        )}
        <div
          className={classNames(
            "grid transition-[grid-template-rows] duration-200 ease-in-out",
            topic.includesIndividualNotes ||
              topic.includesIndividualNotesForCurrentUser
              ? "grid-rows-[1fr]"
              : "grid-rows-[0fr]",
          )}
        >
          <div className="overflow-hidden">
            <div className="pb-10">
              <IndividualNotesContainer
                topic={topic}
                meeting={meeting}
                className={meetingPaddingClassName}
                onModEnter={handleCreateTopicBelow}
              />
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};

const Topic = ({
  topic,
  index,
  meeting,
  opensInSidebar,
  isInExtension,
  isInPresentation,
  currentPageMeeting = null,
  isDiscussed = false,
  onToggleDiscussed,
  autoFocus = false,
}: {
  topic: TopicNodeNewPageFragmentFragment;
  index?: number;
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  opensInSidebar?: boolean;
  isInExtension?: boolean;
  isInPresentation?: boolean;
  currentPageMeeting?: MeetingViewMeetingNodeNewPageFragmentFragment | null;
  isDiscussed?: boolean;
  onToggleDiscussed?: () => void;
  autoFocus?: boolean;
}) => {
  const isInFullScreen = index === undefined;

  return isInFullScreen ? (
    <TopicContent
      key={topic.id}
      topic={topic}
      index={index}
      meeting={meeting}
      opensInSidebar={opensInSidebar}
      isInExtension={isInExtension}
      isInPresentation={isInPresentation}
      currentPageMeeting={currentPageMeeting}
      isDiscussed={isDiscussed}
      onToggleDiscussed={onToggleDiscussed}
      autoFocus={autoFocus}
    />
  ) : (
    <Draggable draggableId={`topic-${topic.id}`} index={index}>
      {(provided, snapshot) => (
        <TopicContent
          provided={provided}
          snapshot={snapshot}
          isInPresentation={isInPresentation}
          key={topic.id}
          topic={topic}
          index={index}
          meeting={meeting}
          opensInSidebar={opensInSidebar}
          isInExtension={isInExtension}
          currentPageMeeting={currentPageMeeting}
          isDiscussed={isDiscussed}
          onToggleDiscussed={onToggleDiscussed}
          autoFocus={autoFocus}
        />
      )}
    </Draggable>
  );
};

const TopicWithSentry = withErrorBoundary(Topic, {
  fallback: <Error description={"The topic could not be rendered."} />,
});

export default TopicWithSentry;
